import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';
import api from "../utils/api";
import "./PatientProfile.css";

const PatientProfile = () => {
  const { patientUser } = useContext(AuthContext);
  const [patient, setPatient] = useState(null);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    gender: "",
    dateOfBirth: "",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      if (!patientUser || patientUser.role !== "patient") {
        setError("You are not authorized to view this profile. Please log in as a patient.");
        return;
      }

      try {
        const response = await api.get("/patients/profile", {
          headers: { Authorization: `Bearer ${patientUser.accessToken}` },
        });
        const patientData = response.data;
        if (patientData._id !== patientUser.id) {
          setError("Profile ID does not match logged-in user.");
          return;
        }

        setPatient(patientData);
        setFormData({
          name: patientData.name || "",
          email: patientData.email || "",
          phone: patientData.phone || "",
          address: patientData.address || "",
          gender: patientData.gender || "",
          dateOfBirth: patientData.dateOfBirth ? patientData.dateOfBirth.split("T")[0] : "",
        });
      } catch (err) {
        setError("Failed to fetch profile: " + (err.response?.data?.message || err.message));
      }
    };

    fetchProfile();
  }, [patientUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!patientUser) {
      setError("Please log in to update your profile.");
      return;
    }
    try {
      const response = await api.put("/patients/profile", formData, {
        headers: { Authorization: `Bearer ${patientUser.accessToken}` },
      });
      setPatient(response.data);
      setIsEditing(false);
      setError("");
    } catch (err) {
      setError("Failed to update profile: " + (err.response?.data?.message || err.message));
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }

    try {
      const response = await api.post(
        "/patients/patient/change-password",
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        },
        { headers: { Authorization: `Bearer ${patientUser.accessToken}` } }
      );
      setPasswordSuccess(response.data.message);
      setPasswordData({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
      setIsChangingPassword(false);
    } catch (err) {
      setPasswordError(err.response?.data?.error || "Failed to change password.");
    }
  };

  if (!patient && !error) return <div className="loading">Loading...</div>;

  return (
    <div className="profile-page">
      <div className="auth-container">
        <h2>Patient Profile</h2>
        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => navigate('/login?type=patient')}>Go to Login</button>
          </div>
        )}
        {patient && !error && (
          <>
            {isEditing ? (
              <form onSubmit={handleSubmit} className="profile-form">
                <div>
                  <label>Name:</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <label>Email:</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <label>Phone:</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label>Address:</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label>Gender:</label>
                  <select name="gender" value={formData.gender} onChange={handleInputChange} required>
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label>Date of Birth:</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="save-btn">Save Changes</button>
                  <button type="button" onClick={() => setIsEditing(false)} className="cancel-btn">
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <p><strong>Name:</strong> {patient.name || "N/A"}</p>
                <p><strong>Email:</strong> {patient.email || "N/A"}</p>
                <p><strong>Phone:</strong> {patient.phone || "N/A"}</p>
                <p><strong>Address:</strong> {patient.address || "N/A"}</p>
                <p><strong>Gender:</strong> {patient.gender || "N/A"}</p>
                <p>
                  <strong>Date of Birth:</strong>{" "}
                  {patient.dateOfBirth ? new Date(patient.dateOfBirth).toLocaleDateString() : "N/A"}
                </p>
                <button onClick={() => setIsEditing(true)} className="edit-btn">Edit Profile</button>
                <button onClick={() => setIsChangingPassword(true)} className="edit-btn">Change Password</button>
              </>
            )}

            {/* Change Password Form */}
            {isChangingPassword && (
              <div className="change-password-section">
                <h3>Change Password</h3>
                {passwordError && <p className="error-message">{passwordError}</p>}
                {passwordSuccess && <p className="success-message">{passwordSuccess}</p>}
                <form onSubmit={handlePasswordSubmit}>
                  <div>
                    <label>Current Password:</label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      required
                      autoComplete="current-password"
                    />
                  </div>
                  <div>
                    <label>New Password:</label>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      required
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label>Confirm New Password:</label>
                    <input
                      type="password"
                      name="confirmNewPassword"
                      value={passwordData.confirmNewPassword}
                      onChange={handlePasswordChange}
                      required
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="save-btn">Update Password</button>
                    <button
                      type="button"
                      onClick={() => setIsChangingPassword(false)}
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PatientProfile;